// src/pages/AchievementDetail.tsx (with debug tool removed)
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import LoadingState from '../components/common/LoadingState';
import { useLanguage } from '../contexts/LanguageContext';
import { getAchievementDetails, Achievement, AchievementObjective } from '../services/achievementService';
import { getAchievementLinks, addAchievementLink, deleteAchievementLink } from '../services/achievementLinksService';
import { isAuthenticated } from '../services/authService';
import { ArrowLeftIcon,HomeIcon } from '@heroicons/react/24/outline';
import AchievementHeader from '../components/achievements/AchievementHeader';
import StepList from '../components/achievements/StepList';
import CommunityLinks, { AchievementLink } from '../components/achievements/CommunityLinks';
import YouTubeModal from '../components/achievements/YouTubeModal';
import SyncProgressButton from '../components/achievements/SyncProgressButton';

interface Step {
  id: number;
  name?: string;
  description?: string;
  completed?: boolean;
  progress?: { current: number; total: number };
}

const AchievementDetail: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [achievement, setAchievement] = useState<Achievement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<AchievementLink[]>([]);
  const [newLink, setNewLink] = useState<{ url: string; title: string; type: 'youtube' | 'website' }>({ url: '', title: '', type: 'website' });
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);
  const [activeYoutubeModal, setActiveYoutubeModal] = useState<AchievementLink | null>(null);
  const [isThresholdAchievement, setIsThresholdAchievement] = useState(false);

  const loadAchievementData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getAchievementDetails(Number(id));
      
      // Wenn data.steps fehlt, aber data.objectives vorhanden ist, konvertieren wir diese
      if (data && !data.steps && data.objectives && data.objectives.length > 0) {
        data.steps = data.objectives.map(obj => ({
          id: obj.id,
          name: obj.name || undefined,
          description: obj.description || undefined,
          completed: obj.is_completed,
          progress: obj.completion_value ? {
            current: obj.progress || 0,
            total: obj.completion_value
          } : undefined
        }));
      }
      
      setAchievement(data);
      
      // Detect if this is a threshold/tiered achievement
      if (data && data.steps && data.steps.length > 1) {
        // Check if this might be a threshold achievement with multiple steps/tiers
        const uniqueDescriptions = new Set(data.steps.map((step: Step) => step.description));
        
        // If there are multiple steps but they all have the same description,
        // or if descriptions follow a pattern typical for threshold achievements
        if (uniqueDescriptions.size === 1 || isThresholdPattern(data.steps)) {
          setIsThresholdAchievement(true);
        }
      }
      
      const linksData = await getAchievementLinks(Number(id));
      setLinks(linksData || []);
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  // Helper function to detect threshold pattern in steps
  const isThresholdPattern = (steps: Step[]): boolean => {
    // Check if the steps seem to have progressive goals (e.g., Kill 5, Kill 10, Kill 15)
    if (steps.length < 2) return false;
    
    // Get completion values if available
    const completionValues = steps.map(step => step.progress?.total || 0)
      .filter(val => val > 0)
      .sort((a, b) => a - b);
    
    // If we have increasing completion values, this is likely a threshold achievement
    if (completionValues.length > 1) {
      // Check if values are consistently increasing
      let isProgressive = true;
      for (let i = 1; i < completionValues.length; i++) {
        if (completionValues[i] <= completionValues[i-1]) {
          isProgressive = false;
          break;
        }
      }
      if (isProgressive) return true;
    }
    
    // Check for threshold pattern in descriptions
    const descriptions = steps.map(step => step.description || '');
    const numberPattern = /\d+/g;
    
    // Extract numbers from all descriptions
    const allNumbers = descriptions.map(desc => {
      const matches = desc.match(numberPattern);
      return matches ? matches.map(n => parseInt(n, 10)) : [];
    });
    
    // Check if there's at least one number in each description
    if (allNumbers.every(nums => nums.length > 0)) {
      // Check if these numbers are increasing across steps
      const firstNumbers = allNumbers.map(nums => nums[0]).sort((a, b) => a - b);
      let isIncreasing = true;
      for (let i = 1; i < firstNumbers.length; i++) {
        if (firstNumbers[i] <= firstNumbers[i-1]) {
          isIncreasing = false;
          break;
        }
      }
      return isIncreasing;
    }
    
    return false;
  };

  useEffect(() => {
    const isUserAuth = isAuthenticated();
    setUserIsAuthenticated(isUserAuth);
    loadAchievementData();
    
    const handleProgressSync = () => {
      loadAchievementData();
    };
    
    window.addEventListener('progress-sync-completed', handleProgressSync);
    return () => window.removeEventListener('progress-sync-completed', handleProgressSync);
  }, [loadAchievementData]);
  
  const handleGoBack = () => {
    const savedPath = sessionStorage.getItem('achievementPath');
    navigate(savedPath || '/achievements');
  };

  const handleAddLink = async () => {
    setLinkError(null);
    if (!newLink.url.trim()) return setLinkError(t('achievements.urlRequired'));
    if (!newLink.title.trim()) return setLinkError(t('achievements.titleRequired'));
    try {
      if (!id) return;
      const linkType = newLink.url.includes('youtube.com') || newLink.url.includes('youtu.be') ? 'youtube' : 'website';
      const addedLink = await addAchievementLink({ achievementId: Number(id), ...newLink, type: linkType });
      if (addedLink) {
        setLinks([...links, addedLink]);
        setNewLink({ url: '', title: '', type: 'website' });
        setIsAddingLink(false);
      }
    } catch (err: any) {
      setLinkError(err.message || t('achievements.failedToAddLink'));
    }
  };

  const handleDeleteLink = async (linkId: number) => {
    try {
      await deleteAchievementLink(linkId);
      setLinks(links.filter(link => link.id !== linkId));
    } catch (err: any) {
      setLinkError(err.message || t('achievements.failedToDeleteLink'));
    }
  };

  const parsedBreadcrumbPath: { id: number; name: string; type: string }[] = (() => {
    try {
      const raw = sessionStorage.getItem('achievementBreadcrumb');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();

  if (loading) return <LoadingState message={t('common.loading')} />;
  if (error || !achievement) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-destiny-red text-xl mb-4">{t('common.error')}</h2>
        <p className="text-gray-300">{error || t('achievements.notFound')}</p>
        <button onClick={handleGoBack} className="inline-flex items-center mb-6 text-destiny-blue hover:text-destiny-gold">
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          {t('common.back')} {t('achievements.title')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {activeYoutubeModal && (
        <YouTubeModal
          title={activeYoutubeModal.title}
          url={activeYoutubeModal.url}
          onClose={() => setActiveYoutubeModal(null)}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-destiny-gold">{t('achievements.title')}</h1>
        {userIsAuthenticated && (
          <SyncProgressButton compact={true} className="ml-2" onSyncComplete={loadAchievementData} />
        )}
      </div>

      <div className="flex items-center mb-6 text-sm">
        <button onClick={() => navigate('/achievements?reset=1')} className="text-gray-400 hover:text-white flex items-center">
          <HomeIcon className="h-4 w-4 mr-1" />
          <span>Home</span>
        </button>
        {parsedBreadcrumbPath.map((item) => (
          <div key={`${item.type}-${item.id}`} className="flex items-center">
            <span className="mx-2 text-gray-500">{'>'}</span>
            <Link
              to={`/achievements?group=${item.type === 'group' ? item.id : parsedBreadcrumbPath.find(p => p.type === 'group')?.id}&category=${item.type === 'category' ? item.id : ''}`}
              className="text-gray-400 hover:text-white"
            >
              {item.name}
            </Link>
          </div>
        ))}
        <div className="flex items-center">
          <span className="mx-2 text-gray-500">{'>'}</span>
          <span className="text-destiny-gold">{achievement.name || '...'}</span>
        </div>
      </div>

      <AchievementHeader
        name={achievement.name}
        description={achievement.description}
        iconUrl={achievement.icon_url}
        isCompleted={achievement.is_completed || false}
        categoryName={achievement.category_name}
        points={achievement.points}
        progress={achievement.progress}
        showProgress={userIsAuthenticated}
      />

<StepList 
        steps={achievement.steps || []} 
        showProgress={userIsAuthenticated}
        isThresholdAchievement={isThresholdAchievement}
      />

      <CommunityLinks
        links={links}
        userIsAuthenticated={userIsAuthenticated}
        isAddingLink={isAddingLink}
        linkError={linkError}
        newLink={newLink}
        onChangeNewLink={(field: 'url' | 'title' | 'type', value: string) =>
          setNewLink(prev => ({ ...prev, [field]: value }))
        }
        onAddLink={handleAddLink}
        onStartAdding={() => setIsAddingLink(true)}
        onCancelAdd={() => setIsAddingLink(false)}
        onDelete={handleDeleteLink}
        onOpenYoutubeModal={setActiveYoutubeModal}
      />
    </div>
  );
};

export default AchievementDetail;